/** Small formatting helpers shared across panels. */

import { STABILITY_MODEL } from '../sim/stability.ts';
import type { TimelineEvent } from '../sim/timeline.ts';

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

export function compassLabel(deg: number): string {
  const idx = ((Math.round(deg / 45) % 8) + 8) % 8;
  return COMPASS[idx]!;
}

/** "NE 42°", or "flat" for NaN aspect. */
export function formatAspect(deg: number): string {
  return Number.isNaN(deg) ? 'flat' : `${compassLabel(deg)} ${Math.round(deg)}°`;
}

const COMPASS16 = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
];

/** Nearest of the 16 compass points for a bearing (deg). */
export function compassLabel16(deg: number): string {
  const idx = ((Math.round(deg / 22.5) % 16) + 16) % 16;
  return COMPASS16[idx]!;
}

/** "WNW 292°" for a bearing (deg), normalised to 0…360. */
export function formatCompass16(deg: number): string {
  const norm = ((deg % 360) + 360) % 360;
  return `${compassLabel16(norm)} ${Math.round(norm)}°`;
}

/** "+12" / "−8" / "0", with an explicit sign (used for signed deltas like wind loading). */
export function formatSigned(v: number, decimals = 0): string {
  if (!Number.isFinite(v)) return '—';
  const rounded = round(v, decimals);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${formatNumber(Math.abs(rounded), decimals)}`;
}

/** Temperature with a proper minus sign: "−8", "0", "4" (no unit). */
export function formatTemperature(v: number, decimals = 0): string {
  if (!Number.isFinite(v)) return '—';
  // `+ 0` turns −0 (e.g. −0.3 rounded) into 0, so it doesn't print as "-0".
  const rounded = round(v, decimals) + 0;
  return rounded < 0 ? `−${formatNumber(-rounded, decimals)}` : formatNumber(rounded, decimals);
}

/** "snow" / "rain" / "mixed (40% snow)" from a snow fraction 0…1. */
export function formatPhase(snowFrac: number): string {
  if (snowFrac >= 0.99) return 'snow';
  if (snowFrac <= 0.01) return 'rain';
  return `mixed (${Math.round(snowFrac * 100)}% snow)`;
}

export function round(v: number, decimals = 0): number {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}

export function formatNumber(v: number, decimals = 0): string {
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatElevation(v: number | null): string {
  return v === null || !Number.isFinite(v) ? '—' : `${formatNumber(v)} m`;
}

/** "6 h" / "2 d" / "2 d 4 h" from a duration in hours. */
export function formatDaysHours(hours: number): string {
  const h = Math.max(0, Math.round(hours));
  const days = Math.floor(h / 24);
  const rem = h % 24;
  if (days <= 0) return `${h} h`;
  return rem === 0 ? `${days} d` : `${days} d ${rem} h`;
}

/** Same as `formatDaysHours`, but spelling out "day(s)" — used in the simple panel. */
export function formatDaysHoursLong(hours: number): string {
  const h = Math.max(0, Math.round(hours));
  const days = Math.floor(h / 24);
  const rem = h % 24;
  if (days <= 0) return `${h} h`;
  const dayWord = days === 1 ? 'day' : 'days';
  return rem === 0 ? `${days} ${dayWord}` : `${days} ${dayWord} ${rem} h`;
}

/** SK38 stability class (Jamieson & Johnston): poor < 1, fair 1–1.5, good ≥ 1.5. */
export type StabilityClass = 'poor' | 'fair' | 'good';

export function skierClass(value: number): StabilityClass {
  if (value < STABILITY_MODEL.poor) return 'poor';
  if (value < STABILITY_MODEL.fair) return 'fair';
  return 'good';
}

/** Local date and time of timeline `hour` after `origin`: "Sat 15 Feb 13:00". */
function realClock(hour: number, origin: Date): string {
  const d = new Date(origin.getTime() + Math.round(hour) * 3_600_000);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day} ${time}`;
}

/**
 * "Day 3 14:00" for an absolute hour on the weather timeline (hour 0 = scenario start) — or,
 * with `origin` (the real time of hour 0, for imported conditions), the local date and time.
 */
export function formatTimelineClock(hour: number, origin: Date | null = null): string {
  if (origin) return realClock(hour, origin);
  const h = Math.max(0, Math.round(hour));
  const day = Math.floor(h / 24) + 1;
  const clock = h % 24;
  return `Day ${day} ${String(clock).padStart(2, '0')}:00`;
}

/** Tick label for the timeline bar: "Day N" on day boundaries, "+N h" otherwise (e.g. the last
 * tick of a timeline that doesn't end on a whole day). */
export function formatTimelineTick(hour: number, origin: Date | null = null): string {
  if (origin) {
    const d = new Date(origin.getTime() + Math.round(hour) * 3_600_000);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
  }
  const h = Math.max(0, Math.round(hour));
  return h % 24 === 0 ? `Day ${h / 24 + 1}` : `+${h} h`;
}

/** One-line summary of a timeline event, used in the timeline bar and the timeline editor's
 * event list. */
export function formatEventSummary(e: TimelineEvent): string {
  if (e.kind === 'storm') {
    return `Storm · ${formatNumber(e.newSnowCm)} cm, ${formatNumber(e.windSpeed, 1)} m/s ${compassLabel16(e.windFromDeg)}`;
  }
  const t = e.temperature;
  return `Fair, ${e.clearSky ? 'clear' : 'cloudy'} · ${formatTemperature(t.lowTemp)}…${formatTemperature(t.highTemp)} °C`;
}
